import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEntityDetails } from '../../api/endpoints/useEntityDetails';
import {
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  Typography,
  IconButton,
} from '@locus-taxy/encore-mui';
import { WorkflowActionModal } from '@locus-taxy/encore-grid';
import { getNestedValue } from '../../utils/objectUtils';
import { useClientId } from '../../hooks/useClientId';
import DetailsSchemaModal from '../DetailsSchemaModal';
import { useWorkflowActions } from '../../hooks/useWorkflowActions';
import { useState, useMemo, useEffect } from 'react';
import EntityDetailsBody from './EntityDetailsBody';
import type { FormSchema } from './types';
import { useEntityMetadata } from '../../api/endpoints/useEntityMetadata';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useModules, type ModulesConfig } from '../../api/endpoints/useModules';
import {
  buildBreadcrumbsFromModules,
  buildModulesNavigation,
} from '../../utils/modulesNavigation';
import { usePreserveSearchParams } from '../../hooks/usePreserveSearchParams';
import { buildClientPrefixedPath } from '../../utils/url';
import { PageHeader } from '@locus-taxy/encore-ui-toolkit';
import { RoutePath } from '../../types/categories';
import {
  KeyboardDoubleArrowRight,
  History as TimelineIcon,
  Cached,
} from '@locus-taxy/encore-mui/icons';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getApiBaseUrl, GOOGLE_MAPS_API_KEY } from '../../config';
import { DateTimeUtils, getTestId } from '@locus-taxy/encore-utils';
import MoreActionsMenu from './MoreActionsMenu';
import EntityStatusChips from './EntityStatusChips';
import EntityTeamChips from './EntityTeamChips';
import { useRouteContext } from '../../hooks/useRouteContext';
import { TestIdAction } from '../../utils/testIdUtils';
import { useTeams } from '../../api/endpoints/useTeams';

type EntityDetailsDataPayload = {
  data?: {
    metadata?: { rootInstanceKey?: string };
    entityData?: { rootMeta?: { rootInstanceKey?: string } };
  };
};

interface EntityDetailsProps {
  entityName?: string;
}

export default function EntityDetails({
  entityName: entityNameOverride,
}: EntityDetailsProps = {}) {
  const { entityName: entityNameParam, entityId } = useParams();
  const clientId = useClientId();
  const apiBaseUrl = useMemo(() => {
    if (!clientId) return '';
    return getApiBaseUrl(clientId);
  }, [clientId]);
  const location = useLocation();
  const routeContext = useRouteContext();
  const resolvedEntityName = entityNameOverride ?? entityNameParam ?? '';
  const isTourView = resolvedEntityName === 'b2cDeliveryTour';
  const tourFields = [
    'id',
    'name',
    'dropOrders',
    'assignmentDetails',
    'date',
    'dropOrderIds',
    'pickupOrderIds',
    'endVisit',
    'plannedSummary',
    'startVisit',
    'timeslot',
    'tourSource',
    'tourStatus',
  ];
  const {
    data: entityDetails,
    isLoading: isInstanceLoading,
    error: instanceError,
    refetch: refetchEntityDetails,
  } = useEntityDetails(
    clientId,
    resolvedEntityName,
    entityId || '',
    isTourView ? tourFields : undefined,
    true // Include relations to fetch timeline aggregations
  );

  // Include _timelineAggregations alongside entityData
  const entityData = useMemo(() => {
    const baseEntityData = entityDetails?.data?.data?.entityData || {};
    const timelineAggregations =
      entityDetails?.data?.data?._timelineAggregations;

    return timelineAggregations
      ? { ...baseEntityData, _timelineAggregations: timelineAggregations }
      : baseEntityData;
  }, [entityDetails?.data?.data]);

  const actions = entityDetails?.data?.actions || [];
  const {
    data: entityMetadata,
    isLoading: isMetadataLoading,
    error: metadataError,
  } = useEntityMetadata(clientId, resolvedEntityName);

  const detailsForm = entityMetadata?.data?.frontend?.detailsView?.form?.schema;
  const entitySchemaInfo = entityMetadata?.data?.entitySchema as unknown as
    | {
        title?: string;
        timelineConfig?: { enabled?: boolean };
        entityPresentationMetadata: {
          additionalColumnConfig: {
            statusFieldPath?: string;
            subStatusFieldPath?: string;
          };
        };
      }
    | undefined;
  const entityTitle = entitySchemaInfo?.title ?? '';
  const isTimelineEnabled = Boolean(entitySchemaInfo?.timelineConfig?.enabled);
  const statusFieldPath =
    entitySchemaInfo?.entityPresentationMetadata?.additionalColumnConfig
      ?.statusFieldPath;
  const subStatusFieldPath =
    entitySchemaInfo?.entityPresentationMetadata?.additionalColumnConfig
      ?.subStatusFieldPath;
  // Fetch teams for team chips display
  const { data: teams = [] } = useTeams(clientId);

  // Combined loading and error states
  const isLoading = isInstanceLoading || isMetadataLoading;
  const error = instanceError || metadataError;

  // Schema modal state
  const [schemaModalOpen, setSchemaModalOpen] = useState(false);

  // Use workflow actions hook for single entity actions
  const {
    currentWorkflowAction,
    isWorkflowLoading,
    modalVariant,
    handleWorkflowFormSubmit,
    handleWorkflowCloseModal,
    handleWorkflowActionClick,
  } = useWorkflowActions({
    clientId,
    entityName: resolvedEntityName,
    entityId: entityId || '',
    refreshData: refetchEntityDetails,
  });

  const handleActionSubmit = (data: Record<string, unknown>) => {
    handleWorkflowFormSubmit(data);
  };

  // Resolve rootInstanceKey for workflow timeline
  const rootInstanceKey: string | undefined = useMemo(() => {
    const entityDetailsData = entityDetails?.data as
      | EntityDetailsDataPayload
      | undefined;
    const metadataRoot =
      entityDetailsData?.data?.metadata?.rootInstanceKey ?? undefined;
    const entityRoot =
      entityDetailsData?.data?.entityData?.rootMeta?.rootInstanceKey ??
      undefined;
    return metadataRoot || entityRoot;
  }, [entityDetails?.data]);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'));
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [mfeRemountKey, setMfeRemountKey] = useState(0);

  useEffect(() => {
    if (!rootInstanceKey || !isTimelineEnabled) {
      setIsTimelineOpen(false);
      return;
    }

    setIsTimelineOpen(isMdUp);
  }, [isMdUp, rootInstanceKey, isTimelineEnabled]);

  // Track last updated timestamp when entity details are fetched
  useEffect(() => {
    if (entityDetails?.data && !isInstanceLoading) {
      setLastUpdatedAt(Date.now());
    }
  }, [entityDetails?.data, isInstanceLoading]);

  // Build breadcrumbs from modules navigation
  const { data: modulesResponse } = useModules(clientId);
  const { leaves: moduleLeaves } = useMemo(() => {
    const modulesData = modulesResponse?.data as ModulesConfig | undefined;
    const rootItems = modulesData?.modules ?? [];
    return buildModulesNavigation(rootItems, clientId);
  }, [modulesResponse?.data, clientId]);

  const moduleCrumbs = useMemo(
    () => buildBreadcrumbsFromModules(location.pathname, moduleLeaves),
    [location.pathname, moduleLeaves]
  );

  // Header props
  const navigate = useNavigate();
  const { getUrlWithSearchParams } = usePreserveSearchParams();
  const { t } = useTranslation();
  const backHref = getUrlWithSearchParams(
    buildClientPrefixedPath(`/entities/${resolvedEntityName}`, clientId),
    ['teams']
  );
  const headerBreadcrumbs = useMemo(() => {
    // If entity not in modules, show fallback breadcrumb with entity name
    if (moduleCrumbs.length === 0) {
      return [{ id: 'fallback', label: resolvedEntityName || '' }];
    }
    return moduleCrumbs.map((c, i) => ({ id: `bc-${i}`, label: c.title }));
  }, [moduleCrumbs, resolvedEntityName]);

  // Header actions
  const showOutboundButton =
    resolvedEntityName === 'b2cDeliveryTour' &&
    entityData.tourStatus === 'STAGED';
  const handleOutboundClick = () => {
    const url = buildClientPrefixedPath(
      `/entities/${resolvedEntityName}/${entityId}/outbound`,
      clientId
    );
    navigate(url);
  };

  const isMapView = resolvedEntityName === 'map';
  const isNetworkMapView = resolvedEntityName === 'networkMap';
  const timelineAvailable = Boolean(rootInstanceKey) && isTimelineEnabled;

  // Tooltip titles
  const openMapDetailsTooltip = 'Open Map Details';
  const networkMapDetailsTooltip = 'Open Network Map Details';
  const outboundTooltip = 'Outbound';

  const timelineTooltip = timelineAvailable
    ? isTimelineOpen
      ? t
        ? t('entityDetails.hideTimeline', { defaultValue: 'Hide timeline' })
        : 'Hide timeline'
      : t
        ? t('entityDetails.showTimeline', { defaultValue: 'Show timeline' })
        : 'Show timeline'
    : t
      ? t('entityDetails.timelineUnavailable', {
          defaultValue: 'Timeline unavailable',
        })
      : 'Timeline unavailable';

  const flatActions = [
    ...(isMapView
      ? [
          {
            id: 'open-map-details',
            label: openMapDetailsTooltip,
            icon: (
              <Tooltip title={openMapDetailsTooltip}>
                <KeyboardDoubleArrowRight />
              </Tooltip>
            ),
            variant: 'outline' as const,
            onClick: () => {
              const url = buildClientPrefixedPath(
                `${RoutePath.MAP_DETAILS}/${entityId}`,
                clientId
              );
              navigate(url);
            },
          },
        ]
      : []),
    ...(isNetworkMapView
      ? [
          {
            id: 'open-network-map-details',
            label: networkMapDetailsTooltip,
            icon: (
              <Tooltip title={networkMapDetailsTooltip}>
                <KeyboardDoubleArrowRight />
              </Tooltip>
            ),
            variant: 'outline' as const,
            onClick: () => {
              const url = buildClientPrefixedPath(
                `${RoutePath.NETWORK_MAP_DETAILS}/${entityId}`,
                clientId
              );
              navigate(url);
            },
          },
        ]
      : []),
    ...(showOutboundButton
      ? [
          {
            id: 'outbound',
            label: outboundTooltip,
            icon: (
              <Tooltip title={outboundTooltip}>
                <KeyboardDoubleArrowRight />
              </Tooltip>
            ),
            variant: 'outline' as const,
            onClick: () => handleOutboundClick(),
          },
        ]
      : []),
  ];

  const dropdownActions = (actions || []).map(action => ({
    type: 'item' as const,
    id: action.id,
    label: action.label,
    onClick: () => handleWorkflowActionClick(action),
  }));

  const handleViewSchemaClick = () => {
    setSchemaModalOpen(true);
  };

  const handleTimelineToggle = () => {
    if (!timelineAvailable) {
      return;
    }

    setIsTimelineOpen(prev => !prev);
  };

  const rightSlot = (
    <MoreActionsMenu onViewSchema={handleViewSchemaClick} t={t} />
  );

  // Format last updated duration with periodic updates
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (!lastUpdatedAt) return;

    // Update every minute to refresh the relative time display
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [lastUpdatedAt]);

  const lastUpdatedDuration = useMemo(() => {
    if (!lastUpdatedAt) return null;
    const dateTimeUtils = DateTimeUtils.getInstance();
    const updatedTime = dateTimeUtils.toDayjs(lastUpdatedAt);
    return dateTimeUtils.formatRelative(updatedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdatedAt, currentTime]);

  // Handle refresh
  const handleRefresh = () => {
    // Update timestamp immediately to show refresh action
    setLastUpdatedAt(Date.now());
    refetchEntityDetails();

    // Increment MFE remount key for custom apps
    setMfeRemountKey(prev => prev + 1);
  };

  const actionStack = [
    ...(isTimelineEnabled
      ? [
          {
            id: 'toggle-timeline',
            icon: (
              <Tooltip title={timelineTooltip}>
                <TimelineIcon
                  color={
                    isTimelineOpen && timelineAvailable ? 'primary' : 'inherit'
                  }
                />
              </Tooltip>
            ),
            ariaLabel: timelineTooltip,
            onClick: handleTimelineToggle,
            disabled: !timelineAvailable,
          },
        ]
      : []),
  ];

  // Status Chip and Team Chips
  // Helper function to safely get and validate a string value
  const getValidatedStatus = (
    path: string | undefined,
    fallback: unknown
  ): string | undefined => {
    if (path) {
      const pathValue = getNestedValue<string>(entityData, path);
      if (typeof pathValue === 'string' && pathValue !== '') {
        return pathValue;
      }
    }
    // Fall back to direct property access with runtime validation
    if (typeof fallback === 'string' && fallback !== '') {
      return fallback;
    }
    return undefined;
  };

  const status = getValidatedStatus(statusFieldPath, entityData?.status);
  const subStatus = getValidatedStatus(
    subStatusFieldPath,
    entityData?.subStatus
  );
  const teamIds = entityData?.teamIds as string[] | undefined;

  const titleAdornment = (
    <Stack direction="row" gap={1} alignItems="center">
      <EntityTeamChips teamIds={teamIds} teams={teams} />
      <EntityStatusChips status={status} subStatus={subStatus} />
    </Stack>
  );

  const workflowRouteContext = useMemo(
    () => ({
      ...routeContext,
      // Add the workflow action label or id to the entity name
      entity: `${routeContext.entity}-${currentWorkflowAction?.label ?? currentWorkflowAction?.id}`,
    }),
    [routeContext, currentWorkflowAction]
  );

  const subheading = lastUpdatedDuration ? (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        // @ts-expect-error - body3 is not a valid variant
        variant="body3"
        color="text.secondary"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'last-updated-time',
          entity: resolvedEntityName || undefined,
          page: 'details',
        })}
      >
        {isInstanceLoading
          ? t('gridView.updating', { defaultValue: 'Updating...' })
          : `Updated ${lastUpdatedDuration}`}
      </Typography>
      <Tooltip
        title={String(t('gridView.refresh', { defaultValue: 'Refresh' }))}
        placement="bottom"
      >
        <IconButton
          size="small"
          onClick={handleRefresh}
          aria-label={String(
            t('gridView.refresh', { defaultValue: 'Refresh' })
          )}
          disabled={isInstanceLoading}
          sx={{ width: 18, height: 18, minWidth: 18, padding: 0 }}
          data-testid={getTestId({
            action: TestIdAction.REFRESH,
            object: 'button',
            entity: resolvedEntityName || undefined,
            page: 'details',
          })}
        >
          <Cached sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : undefined;

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme => theme.zIndex.appBar,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <PageHeader
          title={entityData?.name || entityId || ''}
          titleAdornment={titleAdornment}
          breadcrumbs={headerBreadcrumbs}
          onBackClick={() => navigate(backHref)}
          flatActions={flatActions}
          dropdownActions={dropdownActions}
          actionStack={actionStack}
          subheading={subheading}
          routeContext={routeContext}
          labels={{
            otherActionsLabel: t('schemaModal.actions'),
          }}
          slots={{
            right: rightSlot,
          }}
          leftPadding="4px"
        />
      </Box>
      <PageContainer
        maxWidth="xl"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'entity-details-container',
          entity: resolvedEntityName || undefined,
          page: 'details',
        })}
        sx={{
          overflow: 'hidden',
          '& > .MuiStack-root:first-of-type': {
            mt: 0,
            mb: 0,
            overflow: 'hidden',

            '& > .MuiBox-root': {
              overflow: 'hidden',
            },
          },
        }}
        slots={{
          header: _props => {
            return null;
          },
        }}
      >
        <EntityDetailsBody
          clientId={clientId}
          entityName={resolvedEntityName}
          entityTitle={entityTitle}
          rootInstanceKey={rootInstanceKey}
          entityData={entityData}
          detailsForm={(detailsForm || undefined) as FormSchema | undefined}
          isLoading={isLoading}
          error={error}
          showTimeline={isTimelineOpen && timelineAvailable}
          onTimelineClose={() => setIsTimelineOpen(false)}
          mfeRemountKey={mfeRemountKey}
        />

        {/* Shared Action Modal */}
        <WorkflowActionModal
          entityData={entityData}
          open={!!currentWorkflowAction}
          onClose={handleWorkflowCloseModal}
          form={currentWorkflowAction?.form?.schema}
          onSubmit={handleActionSubmit}
          isLoading={isWorkflowLoading}
          title={currentWorkflowAction?.label}
          entityName={resolvedEntityName}
          actionType="single"
          currentAction={
            currentWorkflowAction
              ? { type: 'single' as const, action: currentWorkflowAction }
              : null
          }
          clientId={clientId}
          apiBaseUrl={apiBaseUrl}
          googleApiKey={GOOGLE_MAPS_API_KEY}
          routeContext={workflowRouteContext}
          {...modalVariant}
        />

        {/* Schema Modal */}
        <DetailsSchemaModal
          open={schemaModalOpen}
          onClose={() => setSchemaModalOpen(false)}
          entityMetadata={entityMetadata}
          entityName={entityTitle}
          actions={actions}
          entityData={entityDetails?.data}
        />
      </PageContainer>
    </Stack>
  );
}
